import { useCallback, useEffect, useRef, useState } from 'react';
import { Track } from 'livekit-client';
import { useLiveKit } from '../livekit/hooks/useLiveKit';
import { StreamingPiP } from './StreamingPiP';

export interface LiveKitVideoProps {
  roomName: string;
  participantName: string;
  mode: 'stream' | 'join';
  /** Custom PiP position */
  pipPosition?: { x: number; y: number };
  /** Viewer theme for different display modes */
  viewerTheme?: 'normal' | 'mini' | 'icon';
  /** Callback when connection state changes */
  onStateChange?: (state: 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error') => void;
}

/**
 * LiveKit Video Streaming Component - SIMPLE VERSION
 *
 * SYSTEM ARCHITECTURE:
 * ===================
 *
 * 🏗️ STREAMING MODEL:
 * - ONE HOST (streamer) → MULTIPLE VIEWERS (participants)
 * - Unidirectional streaming: Host broadcasts to viewers, viewers only receive
 *
 * 📡 MODE: 'stream' (HOST/STREAMER)
 * - Publishes: ✅ Video + Audio tracks (broadcasts to all viewers)
 * - Receives: ❌ No external video/audio (doesn't receive from viewers)
 * - Audio: Muted locally (prevents echo), but streams to others
 * - Display: Mini circular PiP (self-preview)
 *
 * 👀 MODE: 'join' (VIEWERS/PARTICIPANTS)
 * - Publishes: ❌ No video/audio tracks (viewers don't stream)
 * - Receives: ✅ Video + Audio from streamer (sees and hears host)
 * - Audio: Enabled (hears the host)
 * - Display: Draggable PiP window (host's stream)
 */
export const LiveKitVideo: React.FC<LiveKitVideoProps> = ({
  roomName,
  participantName,
  mode,
  viewerTheme = 'normal',
  onStateChange,
}) => {
  // --------------------- STATE ------------------------
  const [hasVideo, setHasVideo] = useState(false);
  const [hasAudio, setHasAudio] = useState(false);
  const [isAudioMuted, setIsAudioMuted] = useState(true);

  // Store reference to video element for manual mute control
  const videoElementRef = useRef<HTMLVideoElement | null>(null);

  // --------------------- LIVEKIT HOOK ------------------------
  const { startStreaming, joinAsViewer, state, room, currentMode, isConnected } = useLiveKit({
    autoReconnect: true, // Re-enable with protections against infinite loops
    reconnectAttempts: 3,
    reconnectDelay: 5000,
    onStateChange: (newState) => {
      console.log('🔍 LiveKit State Change:', newState);
      onStateChange?.(newState);

      // Handle disconnection
      if (newState === 'disconnected') {
        console.log('🔍 Resetting video state on disconnect');
        setHasVideo(false);
        setHasAudio(false);
        setIsAudioMuted(true);
      }
    },
  });

  // Simple function to unmute audio
  const handleUnmuteAudio = useCallback(async () => {
    console.log('🔊 Unmute audio requested');

    if (videoElementRef.current) {
      videoElementRef.current.muted = false;
      setIsAudioMuted(false);

      // Force video to play (this helps with AudioContext activation)
      try {
        await videoElementRef.current.play();
        console.log('🔊 Audio unmuted and video playing');
      } catch (error) {
        console.warn('⚠️ Could not start video playback:', error);
      }

      // Attach all available audio tracks now that user has interacted
      try {
        if (room) {
          console.log('🎵 Attaching audio tracks after user interaction...');

          // Attach audio tracks from all remote participants
          room.remoteParticipants.forEach((participant) => {
            participant.audioTrackPublications.forEach((publication) => {
              if (publication.track && videoElementRef.current) {
                publication.track.attach(videoElementRef.current);
                console.log(`🔊 Audio track attached from ${participant.identity}`);
              }
            });
          });

          // Force LiveKit to start audio processing
          await room.startAudio();
          console.log('🎵 LiveKit audio started successfully');
        }
      } catch (error) {
        console.warn('⚠️ Could not start LiveKit audio:', error);

        // Fallback: try to resume global AudioContext
        try {
          // @ts-expect-error - AudioContext global access
          if (window.AudioContext || window.webkitAudioContext) {
            // @ts-expect-error - AudioContext global access
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            // Try to find any suspended AudioContext and resume it
            console.log('🎵 Attempting to resume global AudioContext...');

            // This is a hack to try to resume any suspended AudioContext
            const tempContext = new AudioContext();
            if (tempContext.state === 'suspended') {
              await tempContext.resume();
              console.log('🎵 AudioContext resumed');
            }
            tempContext.close();
          }
        } catch (fallbackError) {
          console.warn('⚠️ Could not resume AudioContext:', fallbackError);
        }
      }

      // Additional fallback: ensure video element properties are correct
      videoElementRef.current.volume = 1.0;
      videoElementRef.current.muted = false;

      // Debug: Check video element audio properties
      console.log('🔊 Video element audio properties:', {
        muted: videoElementRef.current.muted,
        volume: videoElementRef.current.volume,
        paused: videoElementRef.current.paused,
        readyState: videoElementRef.current.readyState,
        hasAudio: 'unknown', // audioTracks not available on HTMLVideoElement
        currentSrc: videoElementRef.current.currentSrc
          ? `${videoElementRef.current.currentSrc.substring(0, 50)}...`
          : 'none',
      });

      // Try to play a test sound to verify audio is working
      try {
        // @ts-expect-error - AudioContext may not be available in all browsers
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        console.log('🎵 AudioContext state:', audioContext.state);

        if (audioContext.state === 'suspended') {
          await audioContext.resume();
          console.log('🎵 AudioContext resumed successfully');
        }

        // Create a very short test beep
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);

        console.log('🔔 Test beep played - if you heard it, audio is working!');
        audioContext.close();
      } catch (testError) {
        console.warn('⚠️ Could not play test sound:', testError);
      }
    }
  }, [room]);

  // Removed unused memoized values

  // --------------------- VIDEO ATTACHMENT (PURE FUNCTION) ------------------------
  const lastAttachmentRef = useRef<number>(0);

  const handleAttachVideo = useCallback(
    (videoElement: HTMLVideoElement) => {
      if (!room) return;

      // Debounce to prevent rapid re-attachments
      const now = Date.now();
      if (now - lastAttachmentRef.current < 1000) {
        console.log('⏰ Debouncing video attachment (too frequent)');
        return;
      }
      lastAttachmentRef.current = now;

      console.log(`📹 Attaching video element to LiveKit tracks (${mode})`);

      // Store video element reference for manual control
      videoElementRef.current = videoElement;

      // Configure video element
      videoElement.muted = true; // Always start muted to avoid autoplay issues
      videoElement.autoplay = true;
      videoElement.playsInline = true;

      // Event handlers for dynamic track attachment
      const handleTrackSubscribed = (
        track: Track,
        _publication: unknown,
        participant: { identity: string }
      ) => {
        console.log(`📡 Track subscribed: ${track.kind} from ${participant.identity}`);
        if (track.kind === 'video') {
          track.attach(videoElement);
          setHasVideo(true);
          console.log('✅ Video track attached dynamically');
        } else if (track.kind === 'audio') {
          // Don't attach audio automatically - wait for user interaction
          setHasAudio(true);
          console.log('🔊 Audio track detected dynamically (will attach on unmute)');
        }
      };

      const handleLocalTrackPublished = (publication: {
        kind: string;
        track?: { kind: string; attach: (element: HTMLVideoElement) => void };
      }) => {
        console.log(`📤 Local track published: ${publication.kind}`);
        if (mode === 'stream' && publication.track) {
          if (publication.track.kind === 'video') {
            publication.track.attach(videoElement);
            setHasVideo(true);
            console.log('✅ Local video track attached');
          } else if (publication.track.kind === 'audio') {
            setHasAudio(true);

            console.log('🔊 Local audio track detected (not attached to avoid echo)');
          }
        }
      };

      // Add event listeners for dynamic tracks
      room.on('trackSubscribed', handleTrackSubscribed);
      room.on('localTrackPublished', handleLocalTrackPublished);

      // Process existing tracks immediately
      if (mode === 'stream' && room.localParticipant) {
        console.log('🔍 Processing existing local tracks for streamer');
        room.localParticipant.videoTrackPublications.forEach((publication) => {
          if (publication.track) {
            publication.track.attach(videoElement);
            console.log('✅ Local video track attached (stream mode)');
            setHasVideo(true);
          }
        });

        room.localParticipant.audioTrackPublications.forEach((publication) => {
          if (publication.track) {
            setHasAudio(true);

            console.log('🔊 Local audio track detected (not attached to avoid echo)');
          }
        });
      } else if (mode === 'join') {
        console.log(
          `🔍 Processing existing remote tracks for viewer (${room.remoteParticipants.size} participants)`
        );
        room.remoteParticipants.forEach((participant) => {
          console.log(`👤 Checking participant: ${participant.identity}`);
          participant.videoTrackPublications.forEach((publication) => {
            if (publication.track) {
              publication.track.attach(videoElement);
              console.log('✅ Remote video track attached (viewer mode)');
              setHasVideo(true);
            }
          });

          participant.audioTrackPublications.forEach((publication) => {
            if (publication.track) {
              // Don't attach audio automatically - wait for user interaction
              setHasAudio(true);

              console.log('🔊 Remote audio track detected (will attach on unmute)');
            }
          });
        });
      }

      // Return cleanup function
      return () => {
        console.log('🧹 Cleaning up video attachments');
        room.off('trackSubscribed', handleTrackSubscribed);
        room.off('localTrackPublished', handleLocalTrackPublished);
        if (videoElement) {
          videoElement.srcObject = null;
          videoElement.src = '';
        }
      };
    },
    [room, mode] // Minimal dependencies to prevent loops
  );

  // --------------------- EFFECTS ------------------------

  // Auto-connect
  useEffect(() => {
    if (state !== 'idle') return;

    const connect = async () => {
      try {
        console.log(`🚀 Connecting in ${mode} mode...`);
        if (mode === 'stream') {
          await startStreaming(roomName, participantName);
        } else {
          await joinAsViewer(roomName, participantName);
        }
      } catch (error) {
        console.error('❌ Connection failed:', error);
      }
    };

    connect();
  }, [roomName, participantName, mode, state, startStreaming, joinAsViewer]);

  // Debug logs
  useEffect(() => {
    console.log('🔍 LiveKit State:', {
      mode,
      state,
      isConnected,
      hasVideo,
      hasAudio,
      isAudioMuted,
    });
  }, [mode, state, isConnected, hasVideo, hasAudio, isAudioMuted]);

  // --------------------- RENDER ------------------------

  // Map business logic to UI props
  const isStreamerMode = (currentMode || mode) === 'stream';
  const uiTheme = viewerTheme === 'icon' ? 'normal' : (viewerTheme as 'normal' | 'mini');

  return (
    <StreamingPiP
      availableThemes={isStreamerMode ? ['mini'] : ['normal', 'mini']}
      isDraggable={!isStreamerMode && uiTheme === 'normal'}
      connectionState={state}
      hasVideo={hasVideo}
      hasAudio={hasAudio}
      isAudioMuted={isAudioMuted}
      basePosition={{ top: 60, right: 10 }}
      onClose={undefined}
      onThemeChange={
        isStreamerMode
          ? undefined
          : (newTheme) => {
              console.log('Theme change requested:', newTheme);
              // Parent component would handle this
            }
      }
      onAttachVideo={handleAttachVideo}
      onUnmuteAudio={isStreamerMode ? undefined : handleUnmuteAudio}
    />
  );
};
